// src/pages/BannerHomePage.jsx
import React, { useState, useEffect } from "react";
import axios from "../axiosConfig";
import { toast } from "react-toastify";
import {
  ImagePlus,
  Images,
  Link as LinkIcon,
  Save,
  Trash2,
  Upload,
} from "lucide-react";
import "./BannerHomePage.css";

export default function BannerHomePage() {
  const [banners, setBanners] = useState([]);
  const [novoBanner, setNovoBanner] = useState({
    titulo: "",
    link: "",
    ativo: true,
    ordem: 0,
  });
  const [novoArquivo, setNovoArquivo] = useState(null);
  const [arquivosEdicao, setArquivosEdicao] = useState({});

  const carregarBanners = async () => {
    try {
      const res = await axios.get("/admin/banner-home");
      setBanners(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erro ao carregar banners:", err?.response?.data || err);
      toast.error("Erro ao carregar banners.");
    }
  };

  useEffect(() => {
    carregarBanners();
  }, []);

  const criarBanner = async () => {
    try {
      if (!novoArquivo) {
        toast.warning("Selecione uma imagem para o novo banner.");
        return;
      }

      const formData = new FormData();
      formData.append("imagem", novoArquivo);
      formData.append("titulo", novoBanner.titulo || "");
      formData.append("link", novoBanner.link || "#campanha");
      formData.append("ativo", novoBanner.ativo ? "true" : "false");
      formData.append("ordem", novoBanner.ordem || 0);

      await axios.post("/admin/banner-home", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Banner criado com sucesso!");

      setNovoBanner({
        titulo: "",
        link: "",
        ativo: true,
        ordem: 0,
      });
      setNovoArquivo(null);

      await carregarBanners();
    } catch (err) {
      console.error("Erro ao criar banner:", err?.response?.data || err);
      toast.error(err?.response?.data?.error || "Erro ao criar banner.");
    }
  };

  const atualizarBanner = async (banner) => {
    try {
      const formData = new FormData();

      const arquivo = arquivosEdicao[banner.id];
      if (arquivo) {
        formData.append("imagem", arquivo);
      }

      formData.append("titulo", banner.titulo || "");
      formData.append("link", banner.link || "#campanha");
      formData.append("ativo", banner.ativo ? "true" : "false");
      formData.append("ordem", banner.ordem || 0);

      await axios.put(`/admin/banner-home/${banner.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Banner atualizado!");

      setArquivosEdicao((prev) => {
        const novo = { ...prev };
        delete novo[banner.id];
        return novo;
      });

      await carregarBanners();
    } catch (err) {
      console.error("Erro ao atualizar banner:", err?.response?.data || err);
      toast.error(err?.response?.data?.error || "Erro ao atualizar banner.");
    }
  };

  const excluirBanner = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este banner?")) return;

    try {
      await axios.delete(`/admin/banner-home/${id}`);
      toast.success("Banner excluído!");
      await carregarBanners();
    } catch (err) {
      console.error("Erro ao excluir banner:", err?.response?.data || err);
      toast.error(err?.response?.data?.error || "Erro ao excluir banner.");
    }
  };

  const atualizarCampoBanner = (id, campo, valor) => {
    setBanners((prev) =>
      prev.map((b) =>
        b.id === id
          ? {
              ...b,
              [campo]: valor,
            }
          : b
      )
    );
  };

  return (
    <div className="bannerHomeAdmin-page">
      <div className="bannerHomeAdmin-topbar">
        <div>
          <span className="bannerHomeAdmin-kicker">Painel do cliente</span>
          <h2>Banners da Home</h2>
          <p>
            Gerencie os banners exibidos no carrossel do painel do cliente,
            controle ordem, link de destino e status de exibição.
          </p>
        </div>
      </div>

      <section className="bannerHomeAdmin-card">
        <div className="bannerHomeAdmin-cardHeader">
          <div>
            <h3>Novo banner</h3>
            <span>Adicione uma imagem ao carrossel do painel do cliente.</span>
          </div>

          <ImagePlus size={18} />
        </div>

        <div className="bannerHomeAdmin-formGrid">
          <div className="bannerHomeAdmin-field">
            <label>Título interno</label>
            <input
              type="text"
              placeholder="Ex: Copa do Mundo"
              value={novoBanner.titulo}
              onChange={(e) =>
                setNovoBanner((p) => ({ ...p, titulo: e.target.value }))
              }
            />
          </div>

          <div className="bannerHomeAdmin-field">
            <label>Imagem do banner</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setNovoArquivo(e.target.files[0])}
            />
          </div>

          <div className="bannerHomeAdmin-field bannerHomeAdmin-fieldWide">
            <label>Link ao clicar no banner</label>
            <div className="bannerHomeAdmin-inputIcon">
              <LinkIcon size={15} />
              <input
                type="text"
                placeholder="/painel-cliente/catalogo-premios?categoria=Copa%20do%20Mundo"
                value={novoBanner.link}
                onChange={(e) =>
                  setNovoBanner((p) => ({ ...p, link: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="bannerHomeAdmin-field">
            <label>Ordem</label>
            <input
              type="number"
              value={novoBanner.ordem}
              onChange={(e) =>
                setNovoBanner((p) => ({ ...p, ordem: Number(e.target.value) }))
              }
            />
          </div>

          <div className="bannerHomeAdmin-toggleField">
            <label className="bannerHomeAdmin-toggle">
              <input
                type="checkbox"
                checked={novoBanner.ativo}
                onChange={(e) =>
                  setNovoBanner((p) => ({ ...p, ativo: e.target.checked }))
                }
              />

              <span className="bannerHomeAdmin-toggleTrack">
                <span className="bannerHomeAdmin-toggleThumb" />
              </span>

              <span>
                <strong>Banner ativo</strong>
                <small>Exibe este banner no carrossel do cliente.</small>
              </span>
            </label>
          </div>
        </div>

        <div className="bannerHomeAdmin-actions">
          <button
            type="button"
            className="bannerHomeAdmin-btn primary"
            onClick={criarBanner}
          >
            <Upload size={16} />
            Adicionar banner
          </button>
        </div>
      </section>

      <section className="bannerHomeAdmin-card">
        <div className="bannerHomeAdmin-cardHeader">
          <div>
            <h3>Banners cadastrados</h3>
            <span>
              Atualize as imagens, links, ordem de exibição ou remova banners.
            </span>
          </div>

          <Images size={18} />
        </div>

        <div className="bannerHomeAdmin-lista">
          {banners.length === 0 ? (
            <div className="bannerHomeAdmin-empty">
              Nenhum banner cadastrado.
            </div>
          ) : (
            banners.map((banner) => (
              <div key={banner.id} className="bannerHomeAdmin-item">
                <div className="bannerHomeAdmin-preview">
                  {banner.url ? (
                    <img
                      src={banner.url?.startsWith("http") ? banner.url : banner.url}
                      alt={banner.titulo || "Banner"}
                    />
                  ) : (
                    <div className="bannerHomeAdmin-semImagem">
                      Sem imagem
                    </div>
                  )}
                </div>

                <div className="bannerHomeAdmin-itemForm">
                  <div className="bannerHomeAdmin-field">
                    <label>Título interno</label>
                    <input
                      type="text"
                      value={banner.titulo || ""}
                      onChange={(e) =>
                        atualizarCampoBanner(banner.id, "titulo", e.target.value)
                      }
                    />
                  </div>

                  <div className="bannerHomeAdmin-field">
                    <label>Trocar imagem</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setArquivosEdicao((prev) => ({
                          ...prev,
                          [banner.id]: e.target.files[0],
                        }))
                      }
                    />
                  </div>

                  <div className="bannerHomeAdmin-field bannerHomeAdmin-fieldWide">
                    <label>Link</label>
                    <div className="bannerHomeAdmin-inputIcon">
                      <LinkIcon size={15} />
                      <input
                        type="text"
                        value={banner.link || ""}
                        onChange={(e) =>
                          atualizarCampoBanner(banner.id, "link", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="bannerHomeAdmin-field">
                    <label>Ordem</label>
                    <input
                      type="number"
                      value={banner.ordem || 0}
                      onChange={(e) =>
                        atualizarCampoBanner(
                          banner.id,
                          "ordem",
                          Number(e.target.value)
                        )
                      }
                    />
                  </div>

                  <div className="bannerHomeAdmin-toggleField">
                    <label className="bannerHomeAdmin-toggle">
                      <input
                        type="checkbox"
                        checked={!!banner.ativo}
                        onChange={(e) =>
                          atualizarCampoBanner(
                            banner.id,
                            "ativo",
                            e.target.checked
                          )
                        }
                      />

                      <span className="bannerHomeAdmin-toggleTrack">
                        <span className="bannerHomeAdmin-toggleThumb" />
                      </span>

                      <span>
                        <strong>Banner ativo</strong>
                        <small>Disponível no carrossel do cliente.</small>
                      </span>
                    </label>
                  </div>

                  <div className="bannerHomeAdmin-actions item">
                    <button
                      type="button"
                      className="bannerHomeAdmin-btn primary"
                      onClick={() => atualizarBanner(banner)}
                    >
                      <Save size={16} />
                      Salvar alterações
                    </button>

                    <button
                      type="button"
                      className="bannerHomeAdmin-btn danger"
                      onClick={() => excluirBanner(banner.id)}
                    >
                      <Trash2 size={16} />
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}